import React, { useState, useEffect } from 'react';
import { 
    DollarSign, Users, Clock, CheckCircle, XCircle, 
    Search, Filter, ChevronRight, AlertCircle, RefreshCw,
    Download, ExternalLink, Mail, Phone, Database
} from 'lucide-react';
import axios from 'axios';

const EarningsManagement = () => {
    const [overview, setOverview] = useState({ lecturers: [], totals: {} });
    const [withdrawals, setWithdrawals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('requests'); // 'requests' or 'lecturers'
    const [searchTerm, setSearchTerm] = useState('');
    const [processingId, setProcessingId] = useState(null);
    const [message, setMessage] = useState(null);

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            const [overviewRes, withdrawalsRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/earnings/admin/overview`, { headers }),
                axios.get(`${API_BASE_URL}/earnings/admin/withdrawals`, { headers })
            ]);

            setOverview(overviewRes.data);
            setWithdrawals(withdrawalsRes.data);
        } catch (error) {
            console.error('Error fetching earnings data:', error);
            setMessage({ type: 'error', text: 'Failed to load earnings data.' });
        } finally {
            setLoading(false);
        }
    };

    const handleWithdrawalAction = async (requestId, status) => {
        try {
            setProcessingId(requestId);
            const token = localStorage.getItem('token');
            const adminNote = prompt(`Add a note for this ${status === 'approved' ? 'approval' : 'rejection'}:`);
            
            await axios.put(`${API_BASE_URL}/earnings/admin/withdrawals/${requestId}`, 
                { status, adminNote },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setMessage({ type: 'success', text: `Withdrawal request ${status} successfully.` });
            fetchData(); // Refresh data
        } catch (error) {
            console.error('Error updating withdrawal:', error);
            setMessage({ type: 'error', text: error.response?.data?.message || 'Action failed.' });
        } finally {
            setProcessingId(null);
        }
    };

    const handleBackfill = async () => {
        if (!window.confirm('This will scan all confirmed payments and create missing earning records. Continue?')) return;
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API_BASE_URL}/earnings/admin/backfill`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(res.data.message);
            fetchData();
        } catch (error) {
            console.error('Backfill error:', error);
            alert('Backfill failed.');
        } finally {
            setLoading(false);
        }
    };

    const filteredLecturers = overview.lecturers.filter(l => 
        l.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        l.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES'
        }).format(amount);
    };

    if (loading) return (
        <div className="flex flex-col items-center justify-center p-12 space-y-4">
            <RefreshCw className="h-8 w-8 text-indigo-600 animate-spin" />
            <p className="text-gray-500 font-medium">Loading earnings data...</p>
        </div>
    );

    return (
        <div className="space-y-6 animate-fadeIn">
            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
                    <div className="p-3 bg-indigo-50 rounded-xl">
                        <DollarSign className="h-6 w-6 text-indigo-600" />
                    </div>
                    <div>
                        <p className="text-sm text-gray-500 font-medium">Total Payouts</p>
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(overview.totals?.totalPayouts || 0)}</p>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
                    <div className="p-3 bg-amber-50 rounded-xl">
                        <Clock className="h-6 w-6 text-amber-600" />
                    </div>
                    <div>
                        <p className="text-sm text-gray-500 font-medium">Pending Payouts</p>
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(overview.totals?.pendingPayouts || 0)}</p>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
                    <div className="p-3 bg-green-50 rounded-xl">
                        <CheckCircle className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                        <p className="text-sm text-gray-500 font-medium">Paid Out</p>
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(overview.totals?.paidOut || 0)}</p>
                    </div>
                </div>
            </div>

            {message && (
                <div className={`p-4 rounded-xl flex items-center gap-3 animate-slideDown ${
                    message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'
                }`}>
                    {message.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
                    <p className="text-sm font-medium">{message.text}</p>
                    <button onClick={() => setMessage(null)} className="ml-auto text-current opacity-50 hover:opacity-100">&times;</button>
                </div>
            )}

            {/* Main Content Area */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="flex border-b border-gray-100 bg-gray-50/50">
                    <button 
                        onClick={() => setActiveTab('requests')}
                        className={`px-8 py-4 text-sm font-bold transition-all relative ${
                            activeTab === 'requests' 
                            ? 'text-indigo-600' 
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                        Withdrawal Requests
                        {withdrawals.filter(w => w.status === 'pending').length > 0 && (
                            <span className="ml-2 px-2 py-0.5 bg-amber-500 text-white text-[10px] rounded-full">
                                {withdrawals.filter(w => w.status === 'pending').length}
                            </span>
                        )}
                        {activeTab === 'requests' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-indigo-600 rounded-t-full" />}
                    </button>
                    <button 
                        onClick={() => setActiveTab('lecturers')}
                        className={`px-8 py-4 text-sm font-bold transition-all relative ${
                            activeTab === 'lecturers' 
                            ? 'text-indigo-600' 
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                        Lecturer Balances
                        {activeTab === 'lecturers' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-indigo-600 rounded-t-full" />}
                    </button>
                </div>

                <div className="p-6">
                    {/* Search & Filters */}
                    <div className="flex flex-col md:flex-row gap-4 mb-6">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input 
                                type="text"
                                placeholder={`Search ${activeTab === 'requests' ? 'requests' : 'lecturers'}...`}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none"
                            />
                        </div>
                        <button 
                            onClick={handleBackfill}
                            className="flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-xl hover:bg-amber-100 transition-all font-bold text-sm"
                            title="Sync existing payments to earnings table"
                        >
                            <Database className="h-4 w-4" />
                            SYNC EARNINGS
                        </button>
                        <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 transition-all">
                            <Filter className="h-4 w-4" />
                            Filter
                        </button>
                        <button className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all shadow-sm">
                            <Download className="h-4 w-4" />
                            Export
                        </button>
                    </div>

                    {activeTab === 'requests' ? (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="text-left border-b border-gray-50">
                                        <th className="pb-4 font-bold text-gray-400 text-xs uppercase tracking-wider px-4">Lecturer</th>
                                        <th className="pb-4 font-bold text-gray-400 text-xs uppercase tracking-wider px-4">Amount</th>
                                        <th className="pb-4 font-bold text-gray-400 text-xs uppercase tracking-wider px-4">Payment Info</th>
                                        <th className="pb-4 font-bold text-gray-400 text-xs uppercase tracking-wider px-4">Date</th>
                                        <th className="pb-4 font-bold text-gray-400 text-xs uppercase tracking-wider px-4">Status</th>
                                        <th className="pb-4 font-bold text-gray-400 text-xs uppercase tracking-wider px-4 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {withdrawals.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="py-12 text-center text-gray-500 italic">No withdrawal requests found.</td>
                                        </tr>
                                    ) : (
                                        withdrawals.map((w) => (
                                            <tr key={w.id} className="hover:bg-gray-50/50 transition-colors group">
                                                <td className="py-4 px-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="h-10 w-10 bg-indigo-100 rounded-xl flex items-center justify-center text-indigo-700 font-bold">
                                                            {w.lecturer?.name?.charAt(0)}
                                                        </div>
                                                        <div>
                                                            <p className="font-bold text-gray-900">{w.lecturer?.name}</p>
                                                            <p className="text-xs text-gray-500">{w.lecturer?.email}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="py-4 px-4 font-bold text-gray-900">{formatCurrency(w.amount)}</td>
                                                <td className="py-4 px-4">
                                                    <div className="flex flex-col gap-1">
                                                        <span className="text-xs font-bold uppercase text-indigo-600">{w.paymentMethod}</span>
                                                        <span className="text-sm text-gray-600">{w.paymentDetails}</span>
                                                    </div>
                                                </td>
                                                <td className="py-4 px-4 text-sm text-gray-500">
                                                    {new Date(w.createdAt).toLocaleDateString()}
                                                </td>
                                                <td className="py-4 px-4">
                                                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                                        w.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                                        w.status === 'approved' ? 'bg-green-100 text-green-700' :
                                                        'bg-red-100 text-red-700'
                                                    }`}>
                                                        {w.status}
                                                    </span>
                                                </td>
                                                <td className="py-4 px-4 text-right">
                                                    {w.status === 'pending' ? (
                                                        <div className="flex justify-end gap-2">
                                                            <button 
                                                                onClick={() => handleWithdrawalAction(w.id, 'rejected')}
                                                                disabled={processingId === w.id}
                                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all disabled:opacity-50"
                                                                title="Reject"
                                                            >
                                                                <XCircle className="h-5 w-5" />
                                                            </button>
                                                            <button 
                                                                onClick={() => handleWithdrawalAction(w.id, 'approved')}
                                                                disabled={processingId === w.id}
                                                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-all disabled:opacity-50"
                                                                title="Approve & Mark Paid"
                                                            >
                                                                <CheckCircle className="h-5 w-5" />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-gray-400 italic">No actions</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="text-left border-b border-gray-50">
                                        <th className="pb-4 font-bold text-gray-400 text-xs uppercase tracking-wider px-4">Lecturer</th>
                                        <th className="pb-4 font-bold text-gray-400 text-xs uppercase tracking-wider px-4">Total Earned</th>
                                        <th className="pb-4 font-bold text-gray-400 text-xs uppercase tracking-wider px-4">Available</th>
                                        <th className="pb-4 font-bold text-gray-400 text-xs uppercase tracking-wider px-4">Paid Out</th>
                                        <th className="pb-4 font-bold text-gray-400 text-xs uppercase tracking-wider px-4">Transactions</th>
                                        <th className="pb-4 font-bold text-gray-400 text-xs uppercase tracking-wider px-4 text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {filteredLecturers.map((l) => (
                                        <tr key={l.id} className="hover:bg-gray-50/50 transition-colors group">
                                            <td className="py-4 px-4">
                                                <div className="flex items-center gap-3">
                                                    {l.profilePicture ? (
                                                        <img src={l.profilePicture} className="h-10 w-10 rounded-xl object-cover" alt="" />
                                                    ) : (
                                                        <div className="h-10 w-10 bg-indigo-100 rounded-xl flex items-center justify-center text-indigo-700 font-bold">
                                                            {l.name?.charAt(0)}
                                                        </div>
                                                    )}
                                                    <div>
                                                        <p className="font-bold text-gray-900">{l.name}</p>
                                                        <p className="text-xs text-gray-500">{l.email}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-4 px-4 font-bold text-gray-900">{formatCurrency(l.totalEarned)}</td>
                                            <td className="py-4 px-4">
                                                <span className="text-amber-600 font-bold">{formatCurrency(l.available)}</span>
                                            </td>
                                            <td className="py-4 px-4 font-medium text-green-600">{formatCurrency(l.paid)}</td>
                                            <td className="py-4 px-4 text-sm text-gray-500">{l.transactionCount} payments</td>
                                            <td className="py-4 px-4 text-right">
                                                <button className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all">
                                                    <ChevronRight className="h-5 w-5" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EarningsManagement;
